// AiPromptTab — system prompt + multi-layer JSON-exempel + tips för
// bättre AI-output. Extraherad ur AiJsonDialog (#4 slice 2).

import { CopyButton } from './CopyButton';

interface AiPromptTabProps {
  systemPrompt: string;
  multiLayerExample: string;
}

export function AiPromptTab({ systemPrompt, multiLayerExample }: AiPromptTabProps) {
  return (
    <div className="mt-0 space-y-5 pr-4">
      <h3 className="text-lg font-semibold">AI System Prompt</h3>
      <p className="text-muted-foreground">
        Copy this prompt and paste it as system instructions (or first message) in any AI chat.
        The prompt contains the complete schema, constraints, and design principles.
      </p>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm">System Prompt</h4>
          <CopyButton text={systemPrompt} label="Copy prompt" />
        </div>
        <pre data-copy-target="Copy prompt" className="p-4 rounded-lg bg-muted font-mono text-xs whitespace-pre-wrap break-words max-h-[400px] overflow-y-auto border border-border">
          {systemPrompt}
        </pre>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm">Multi-Layer Example JSON</h4>
          <CopyButton text={multiLayerExample} label="Copy example" />
        </div>
        <p className="text-xs text-muted-foreground mb-2">
          A complete 3-layer e-commerce architecture (Storefront → Inventory → Notifications). Import directly, or give it to the AI as a reference for the expected output structure.
        </p>
        <pre data-copy-target="Copy example" className="p-4 rounded-lg bg-muted font-mono text-xs whitespace-pre-wrap break-words max-h-[300px] overflow-y-auto border border-border">
          {multiLayerExample}
        </pre>
      </div>

      <div>
        <h4 className="font-medium text-sm mb-3">Tips for Best Results</h4>
        <ul className="space-y-2 text-sm">
          <li className="p-3 rounded-lg bg-accent/10 border border-accent/20">
            <strong>Be specific about structure:</strong> "Create a root with 4 collapsed groups, each containing 3-5 blocks" produces better results than "make a big flow"
          </li>
          <li className="p-3 rounded-lg bg-accent/10 border border-accent/20">
            <strong>Mention colors and icons:</strong> "Use blue (#06b6d4) for data blocks and red (#ef4444) for security. Use shield icon for auth steps."
          </li>
          <li className="p-3 rounded-lg bg-accent/10 border border-accent/20">
            <strong>Request connector labels:</strong> "Label all condition outputs with yes/no or success/failure"
          </li>
          <li className="p-3 rounded-lg bg-accent/10 border border-accent/20">
            <strong>Use multimodal models:</strong> Attach images of existing diagrams, whiteboards, or architecture docs — the AI can convert them
          </li>
          <li className="p-3 rounded-lg bg-accent/10 border border-accent/20">
            <strong>Iterate:</strong> Start with a basic flow, then ask "add more detail to the Auth group" or "nest a sub-layer inside Deploy"
          </li>
          <li className="p-3 rounded-lg bg-accent/10 border border-accent/20">
            <strong>Ask for raw JSON only:</strong> If the AI wraps output in ```json fences, say "output raw JSON only, no markdown"
          </li>
        </ul>
      </div>
    </div>
  );
}
